import { RefObject, useCallback } from "react";

import {
  BulkActionItemID,
  BulkActionsResult,
  BulkActionsStep,
  BulkActionsVariant,
} from "components/BulkActions/types";
import { Worker, WorkerPool } from "types/generated";
import useAnalytics from "hooks/useAnalytics";
import { AnalyticsPageWorkerPool } from "hooks/useAnalytics/pages/workerPool";
import useBulkActions from "components/BulkActions/useBulkActions";
import { BulkActionItemTriggerResult } from "components/BulkActions/helpers";
import useExecutionQueue from "components/BulkActions/useExecutionQueue";
import { getDefaultOnContinueWith } from "components/BulkActions/Drawer/ResultsStep/helpers";
import { BulkActionResultsStepContinueWith } from "components/BulkActions/Drawer/ResultsStep/types";
import BulkActions from "components/BulkActions";
import BulkActionsFloatingBar from "components/BulkActions/FloatingBar";
import BulkActionsFloatingBarHeader from "components/BulkActions/FloatingBar/Header";
import BulkActionsFloatingBarSteps from "components/BulkActions/FloatingBar/Steps";
import BulkActionsPatternsActionsList from "components/BulkActionsPatterns/ActionsList";
import BulkActionsDrawer from "components/BulkActions/Drawer";
import BulkActionsDrawerActionSteps from "components/BulkActions/Drawer/ActionSteps";
import BulkActionsDrawerActionStepsHeader from "components/BulkActions/Drawer/ActionSteps/Header";
import BulkActionsDrawerActionStepsInfoBanner from "components/BulkActions/Drawer/ActionSteps/InfoBanner";
import BulkActionsDrawerResultsStep from "components/BulkActions/Drawer/ResultsStep";
import BulkActionsDrawerResultsStepHeader from "components/BulkActions/Drawer/ResultsStep/Header";
import BulkActionsDrawerResultsStepBody from "components/BulkActions/Drawer/ResultsStep/Body";
import BulkActionsDrawerResultsStepBodyEmptyTab from "components/BulkActions/Drawer/ResultsStep/Body/EmptyTab";
import BulkActionsDrawerResultsStepFooter from "components/BulkActions/Drawer/ResultsStep/Footer";
import { useResultTabItems } from "components/BulkActions/useResultTabItems";
import useCanDrainWorkerPool from "shared/WorkerPool/access/useCanDrainWorkerPool";

import useWorkerBulkActions from "./useWorkerBulkActions";
import { PrivateWorkerPoolWorkersBulkActionsAnalyticsPayload } from "./types";
import PrivateWorkerPoolWorkersBulkActionsConfirmActionForm from "./ConfirmActionForm";
import PrivateWorkerPoolWorkersBulkActionsSelectedItemsView from "./SelectedItemsView";
import PrivateWorkerPoolWorkersBulkActionsConfirmationView from "./ConfirmationView";
import PrivateWorkerPoolWorkersBulkActionsResultItem from "./ResultItem";

type PrivateWorkerPoolWorkersBulkActionsProps = {
  virtualizedListContainerRef: RefObject<HTMLElement | null>;
  selectedSet: Set<BulkActionItemID>;
  workersMap: Map<BulkActionItemID, Worker>;
  onBulkResetAll: () => void;
  onFinish: () => Promise<unknown>;
  onItemDismiss: (id: BulkActionItemID) => void;
  onBulkContinueWith: (continueWith: Set<BulkActionItemID>) => void;
  workerPool: WorkerPool;
};

const PrivateWorkerPoolWorkersBulkActions = ({
  virtualizedListContainerRef,
  selectedSet,
  workersMap,
  onFinish,
  onBulkResetAll,
  onBulkContinueWith,
  onItemDismiss,
  workerPool,
}: PrivateWorkerPoolWorkersBulkActionsProps) => {
  const selectedItemsCount = selectedSet.size;

  const trackSegmentAnalyticsEvent = useAnalytics({
    page: AnalyticsPageWorkerPool.WorkerPoolWorkers,
  });

  const canDrainWorkerPoolsAccess = useCanDrainWorkerPool()(workerPool);

  const workerBulkActions = useWorkerBulkActions(workerPool.id, canDrainWorkerPoolsAccess);

  const {
    selectedBulkAction,
    itemsPerAction,
    availableActions,
    resetSelectedAction,
    skippedSelectedEntityItems,
    selectedEntityItems,
    actionsPerItem,
    applicableSelectedEntityItems,
  } = useBulkActions({
    actions: workerBulkActions,
    selectedSet,
    entityItemsMap: workersMap,
    analyticsPage: AnalyticsPageWorkerPool.WorkerPoolWorkers,
  });

  const onItemTrigger = useCallback(
    (id: BulkActionItemID): Promise<BulkActionItemTriggerResult> => {
      const worker = workersMap.get(id);
      if (!worker || !selectedBulkAction) {
        return Promise.reject();
      }
      if (!selectedBulkAction.condition(worker).result) {
        return Promise.resolve(BulkActionsResult.Skipped);
      }
      return selectedBulkAction.mutation(worker, undefined);
    },
    [workersMap, selectedBulkAction]
  );

  const {
    triggerExecution,
    bulkActionItemResults,
    stopExecution,
    stopExecutionForItem,
    bulkActionResultsMetadata,
    resetBulkActionsExecutionQueue,
  } = useExecutionQueue({
    onItemTrigger,
    onAllItemsFinished: onFinish,
    analyticsPage: AnalyticsPageWorkerPool.WorkerPoolWorkers,
  });

  const { resultsTabItems, saveEntityMap, resetEntityMap } =
    useResultTabItems<Worker>(bulkActionItemResults);

  const handleClose = useCallback(() => {
    onBulkResetAll();
    resetSelectedAction();
    resetBulkActionsExecutionQueue();
    resetEntityMap();
    stopExecution();
  }, [
    onBulkResetAll,
    resetBulkActionsExecutionQueue,
    resetEntityMap,
    resetSelectedAction,
    stopExecution,
  ]);

  const handleOnConfirm = useCallback(
    (analyticsPayload: PrivateWorkerPoolWorkersBulkActionsAnalyticsPayload) => {
      if (selectedBulkAction) {
        const items = itemsPerAction.get(selectedBulkAction.key) || [];
        const skippedItems = skippedSelectedEntityItems.map((item) => item.id);

        triggerExecution(items, undefined, skippedItems);
        saveEntityMap(workersMap);

        trackSegmentAnalyticsEvent("Bulk Actions - Action Confirmed", {
          applicableCount: items?.length || 0,
          notApplicableCount: skippedItems.length,
          action: selectedBulkAction.key,
          ...analyticsPayload,
        });
      }
    },
    [
      itemsPerAction,
      saveEntityMap,
      selectedBulkAction,
      skippedSelectedEntityItems,
      trackSegmentAnalyticsEvent,
      triggerExecution,
      workersMap,
    ]
  );

  const handleOnCancel = (variant: BulkActionsVariant) => () => {
    if (selectedBulkAction) {
      resetSelectedAction();
      const items = itemsPerAction.get(selectedBulkAction.key);
      trackSegmentAnalyticsEvent("Bulk actions - Action Cancelled", {
        applicableCount: items?.length || 0,
        notApplicableCount: skippedSelectedEntityItems.length,
        action: selectedBulkAction.key,
        view: variant,
      });
    }
  };

  const handleContinueWith = useCallback(
    (continueWith: BulkActionResultsStepContinueWith) => {
      getDefaultOnContinueWith(resultsTabItems, onBulkContinueWith)(continueWith);
      resetSelectedAction();
      resetBulkActionsExecutionQueue();
    },
    [onBulkContinueWith, resetSelectedAction, resultsTabItems, resetBulkActionsExecutionQueue]
  );

  return (
    <BulkActions
      selectedItemsCount={selectedItemsCount}
      onContinueWith={handleContinueWith}
      onClose={handleClose}
      isExecutingBulkActions={false}
    >
      <BulkActionsFloatingBar virtualizedListContainerRef={virtualizedListContainerRef}>
        <BulkActionsFloatingBarHeader
          selectedItemsCount={selectedItemsCount}
          applicableItemsCount={
            selectedBulkAction?.key ? itemsPerAction.get(selectedBulkAction.key)?.length : 0
          }
          analyticsPage={AnalyticsPageWorkerPool.WorkerPoolWorkers}
        />
        <BulkActionsFloatingBarSteps>
          {!selectedBulkAction && (
            <BulkActionsPatternsActionsList
              variant={BulkActionsVariant.FloatingBar}
              availableActions={availableActions}
              onEmptyActionsCancel={handleClose}
            />
          )}

          {selectedBulkAction && (
            <PrivateWorkerPoolWorkersBulkActionsConfirmActionForm
              variant={BulkActionsVariant.FloatingBar}
              action={selectedBulkAction}
              onConfirm={handleOnConfirm}
              onCancel={handleOnCancel(BulkActionsVariant.FloatingBar)}
            />
          )}
        </BulkActionsFloatingBarSteps>
      </BulkActionsFloatingBar>

      <BulkActionsDrawer>
        <BulkActionsDrawerActionSteps>
          <BulkActionsDrawerActionStepsHeader
            analyticsPage={AnalyticsPageWorkerPool.WorkerPoolWorkers}
            step={
              !selectedBulkAction
                ? BulkActionsStep.ChooseAction
                : BulkActionsStep.ActionConfirmation
            }
          />
          <BulkActionsDrawerActionStepsInfoBanner />

          {!selectedBulkAction && (
            <PrivateWorkerPoolWorkersBulkActionsSelectedItemsView
              items={selectedEntityItems}
              onItemDismiss={onItemDismiss}
              availableActions={availableActions}
              actionsPerItem={actionsPerItem}
              onEmptyActionsCancel={handleClose}
            />
          )}

          {selectedBulkAction && (
            <PrivateWorkerPoolWorkersBulkActionsConfirmationView
              applicableItems={applicableSelectedEntityItems}
              skippedItems={skippedSelectedEntityItems}
              onItemDismiss={onItemDismiss}
              selectedBulkAction={selectedBulkAction}
              onConfirm={handleOnConfirm}
              onCancel={handleOnCancel(BulkActionsVariant.Drawer)}
            />
          )}
        </BulkActionsDrawerActionSteps>

        <BulkActionsDrawerResultsStep>
          {({ currentTab, setCurrentTab }) => (
            <>
              <BulkActionsDrawerResultsStepHeader
                title={selectedBulkAction?.resultTitle || "Results"}
                analyticsPage={AnalyticsPageWorkerPool.WorkerPoolWorkers}
              />

              <BulkActionsDrawerResultsStepBody
                currentTab={currentTab}
                setCurrentTab={setCurrentTab}
                counter={resultsTabItems}
              >
                {() =>
                  resultsTabItems[currentTab].count === 0 ? (
                    <BulkActionsDrawerResultsStepBodyEmptyTab currentTab={currentTab} />
                  ) : (
                    resultsTabItems[currentTab].entitiesList.map(({ status, items }) =>
                      items.map((item) => (
                        <PrivateWorkerPoolWorkersBulkActionsResultItem
                          key={item.id}
                          item={item}
                          resultsMetadata={bulkActionResultsMetadata}
                          stopExecution={() => stopExecutionForItem(item.id)}
                          status={status}
                        />
                      ))
                    )
                  )
                }
              </BulkActionsDrawerResultsStepBody>

              <BulkActionsDrawerResultsStepFooter
                currentTab={currentTab}
                bulkActionItemResults={bulkActionItemResults}
                onContinueEnabled
                stopExecution={stopExecution}
                analyticsPage={AnalyticsPageWorkerPool.WorkerPoolWorkers}
              />
            </>
          )}
        </BulkActionsDrawerResultsStep>
      </BulkActionsDrawer>
    </BulkActions>
  );
};

export default PrivateWorkerPoolWorkersBulkActions;
